/**
 * The PRIVATE admin note on a suggestion — read by the show and edit pages,
 * written only by the edit page.
 *
 * WHY A SEPARATE TABLE. The note lives in public.suggestion_admin_notes, never on
 * `suggestions`: "member read own" hands the author every column of their own
 * row, and members and admins are the same Postgres role, so a note column on
 * `suggestions` would be readable by the member it is about via
 * `?select=admin_note` however carefully the app hid it. The side table has an
 * admin-only policy, so a member reads zero rows of it.
 *
 * WHY THE WRITE BYPASSES REFINE. Refine's supabase data provider has no upsert,
 * and its update() on a suggestion that has no note yet matches 0 rows and
 * resolves as a success — the first note would vanish silently. So the write is
 * a plain supabaseClient upsert keyed on suggestion_id (the cm grading.ts
 * precedent). The server stamps updated_at / updated_by in a trigger, so the
 * payload is only { suggestion_id, note }.
 *
 * BLANK MEANS DELETE. Clearing the box and saving removes the note row, so
 * "no note" is always the absence of a row, never an empty string (the column
 * is NOT NULL anyway). Both writes ask PostgREST to return the rows they
 * touched: a write that RLS silently filtered to zero rows is reported as a
 * failure instead of a false "saved".
 */
import { useList } from '@refinedev/core'
import { supabaseClient } from '../../lib/supabaseClient'

/** Matches the server CHECK (char_length(note) <= 4000). */
export const NOTE_MAX = 4000

export interface SuggestionAdminNote {
  suggestion_id: string
  note: string
  updated_at: string
  updated_by: string | null
}

export type AdminNoteState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'loaded'; note: SuggestionAdminNote | null }

/**
 * The note for one suggestion (0 or 1 rows). Loaded data wins over a failed
 * background refetch — only a first fetch that failed reads as 'error', and the
 * edit page then refuses to write a note it could not read (a save would
 * otherwise overwrite a note the admin never saw).
 */
export function useSuggestionAdminNote(suggestionId: string | undefined): AdminNoteState {
  const { result, query } = useList<SuggestionAdminNote>({
    resource: 'suggestion_admin_notes',
    filters: [{ field: 'suggestion_id', operator: 'eq', value: suggestionId }],
    pagination: { pageSize: 1 },
    queryOptions: { enabled: !!suggestionId },
  })
  if (!suggestionId) return { kind: 'loading' }
  if (query.data !== undefined) {
    return { kind: 'loaded', note: result.data.find((n) => n.suggestion_id === suggestionId) ?? null }
  }
  return query.isError ? { kind: 'error' } : { kind: 'loading' }
}

/** Trimmed, so a whitespace-only note counts as blank. */
export function normalizeNote(text: string | null | undefined): string {
  return (text ?? '').trim()
}

/**
 * Writes the note: a non-blank note is upserted, a blank one deletes the row.
 * Throws an Error carrying PostgREST's message (its errors are plain objects,
 * not Error instances) — the caller decides how loudly to report it.
 */
export async function writeSuggestionAdminNote(suggestionId: string, text: string): Promise<'saved' | 'removed'> {
  const note = normalizeNote(text)
  if (note) {
    const { data, error } = await supabaseClient
      .from('suggestion_admin_notes')
      .upsert({ suggestion_id: suggestionId, note }, { onConflict: 'suggestion_id' })
      .select('suggestion_id')
    if (error) throw new Error(error.message || 'The note could not be saved.')
    if (!data || data.length === 0) throw new Error('The note was not saved — the server accepted no row.')
    return 'saved'
  }
  const { data, error } = await supabaseClient
    .from('suggestion_admin_notes')
    .delete()
    .eq('suggestion_id', suggestionId)
    .select('suggestion_id')
  if (error) throw new Error(error.message || 'The note could not be removed.')
  if (!data || data.length === 0) throw new Error('The note was not removed — the server deleted no row.')
  return 'removed'
}
